/**
 * #3005 — other tabs follow a switch of account (founder D2 a: one
 * account per browser).
 *
 * Mounted once under `<AuthProvider/>` (`App.tsx`). In a Clerk or dev-shim
 * tab it listens for a switch made in another tab and follows it at the
 * next safe moment (`lib/accountSwitch.ts`, the #2919 rule): at once when
 * nobody types, else on the next page change or when the tab is hidden.
 * After that full load it shows one line, "Now signed in as ‹email›", for
 * a few seconds once the tab is in view. A demo or Telegram tab does not
 * follow.
 */
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../lib/AuthContext";
import {
  consumeNowSignedInAs,
  noteRouteChangeForAccountSwitch,
  startAccountSwitchFollower,
} from "../lib/accountSwitch";

const NOTICE_MS = 8_000;

export function AccountSwitchNotice() {
  const { mode } = useAuth();
  const follows = mode === "clerk" || mode === "dev-shim";
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);
  const [label, setLabel] = useState<string | null>(() => consumeNowSignedInAs());

  useEffect(() => {
    if (!follows) return;
    return startAccountSwitchFollower();
  }, [follows]);

  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    noteRouteChangeForAccountSwitch();
  }, [pathname]);

  // The tab that follows is usually hidden when it loads (the switch came
  // from another tab): count the seconds only while the person can see it.
  useEffect(() => {
    if (!label) return;
    let timer: number | null = null;
    const startWhenSeen = () => {
      if (timer !== null || document.visibilityState === "hidden") return;
      timer = window.setTimeout(() => setLabel(null), NOTICE_MS);
    };
    startWhenSeen();
    document.addEventListener("visibilitychange", startWhenSeen);
    return () => {
      document.removeEventListener("visibilitychange", startWhenSeen);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [label]);

  if (!label) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
      <output
        data-testid="account-switch-notice"
        className="motion-state-in pointer-events-auto flex items-center gap-3 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg shadow-lg"
      >
        <span>Now signed in as {label}</span>
        <button
          type="button"
          onClick={() => setLabel(null)}
          aria-label="Close"
          className="rounded-sm text-fg-subtle hover:text-fg"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </output>
    </div>
  );
}
